import clsx from 'clsx';
import qs from 'query-string';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MainDashboardLayoutComponent } from 'components/main-layout/main.component';
import { ButtonSubmit } from 'components/button/button-submit.component';
import { TableSearchInputComponent } from 'components/table/table-search.component';
import { TableButtonExport } from 'components/table/table-button-export.component';
import { TableButtonLimitComponent } from 'components/table/table-button-limit.component';
import { TableButtonSortComponent } from 'components/table/table-button-sort.component';
import { BasicTableComponent } from 'components/table/table-basic.component';
import { Pagination } from 'components/table/table-pagination.component';
import { EmptyStateComponent } from 'components/empty-data';
import { BounceLoading } from 'components/loader/bounce.loading';
import { ModalTableSortComponent } from 'components/modal/modal-table-sort.component';
import { ModalTableLimitComponent } from 'components/modal/modal-table-limit.component';
import { ModalConfirmation } from 'components/modal/moda.confirmation.component';
import { setToolsReducer } from 'reducers/tools.reducer';
import { StandartImageComponent } from 'components/image/standart-image.component';
import { useProductList } from 'api/product/product-list/product-list.query.api';
import { catchErrorMessage, convertToRupiah } from 'ui-utils/string.utils';
import { useExportProductList } from 'api/product/product-list/product-list.mutation.api';
import { TableButtonImport } from 'components/table/table-button-import.component';
import { PATH_CONSTANT } from 'config/path.constant';
import { Tooltip } from 'flowbite-react';
import { HighlightedText } from 'components/highlighted-text';

const BREAD_CUMB_ITEMS = [
  {
    title: 'Product',
    path: PATH_CONSTANT.PRODUCT.PRODUCT_LIST,
  },
  {
    title: 'Product List',
    path: PATH_CONSTANT.PRODUCT.PRODUCT_LIST,
  },
];

const SORT_OPTIONS = [
  { label: 'Urutkan Nama A-Z', sortType: 'asc', sortBy: 'name' },
  { label: 'Urutkan Nama Z-A', sortType: 'desc', sortBy: 'name' },
  { label: 'Harga Tertinggi - Terendah', sortType: 'desc', sortBy: 'price' },
  { label: 'Harga Terendah - Tertinggi', sortType: 'asc', sortBy: 'price' },
  { label: 'Tanggal buat - Terbaru', sortType: 'desc', sortBy: 'created_at' },
  { label: 'Tanggal buat - Terdahulu', sortType: 'asc', sortBy: 'created_at' },
];

export const ProductListComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const INITIAL_VALUE_FILTER_QUERY = {
    limit: searchParams.get('limit')?.replace(/[^0-9]/g, '') || 20,
    page: searchParams.get('page')?.replace(/[^0-9]/g, '') || 1,
    orderBy: searchParams.get('orderBy')?.replace(/[^a-zA-Z_]/g, '') || 'created_at',
    orderType: searchParams.get('orderType')?.replace(/[^a-zA-Z]/g, '') || 'desc',
    search: searchParams.get('search')?.replace(/[^a-zA-Z0-9 ]/g, '') || undefined,
  };
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [queryFilter, setQueryFilter] = useState(INITIAL_VALUE_FILTER_QUERY);

  const { selectedRowKeys: selectedIds } = useSelector((state) => state.tools);

  const [modals, setModals] = useState({
    sort: false,
    limit: false,
    export: false,
    filter: false,
  });

  const { mutate: exportProduct, isLoading: isExporting } = useExportProductList();

  const {
    data: productList,
    isLoading,
    isError,
    error,
  } = useProductList({
    filter: queryFilter,
  });

  const columns = useMemo(
    () => [
      {
        type: 'checkbox',
        isRightBorderAvailable: true,
        render: (record) => {
          return (
            <div className='form-check px-2'>
              <input
                className='form-check-input appearance-none h-4 w-4 border border-green rounded-sm bg-white checked:bg-green checked:border-green focus:outline-none transition duration-200 align-top bg-no-repeat bg-center bg-contain float-left cursor-pointer'
                type='checkbox'
                readOnly
              />
            </div>
          );
        },
      },

      {
        name: 'Product',
        className: 'w-[25%] text-sm font-semibold text-green ',
        dataIndex: 'name',
        render: (record) => (
          <div class='w-[25%] text-sm font-semibold text-green'>
            <div class='flex items-center'>
              <div class='w-12'>
                <StandartImageComponent
                  defaultImage='/img/default-product.jpg'
                  src={record?.product_image}
                  class='w-12 h-12 object-cover'
                />
              </div>
              <div class='w-[calc(100%-3rem)] pl-2 pr-4'>
                <Link to={`${PATH_CONSTANT.PRODUCT.PRODUCT_EDIT}/${record?.id}`}>
                  <Tooltip className='max-w-[30%]' content={record?.name}>
                    <div class='block text-xs font-semibold text-dark hover:text-green line-clamp-3'>
                      <HighlightedText text={record?.name} highlight={queryFilter?.search} />
                    </div>
                  </Tooltip>
                </Link>
              </div>
            </div>
          </div>
        ),
      },
      {
        name: 'SKU Product',
        className: 'w-[15%] text-sm font-semibold text-green',
        dataIndex: 'address',
        render: (record) => (
          <div class='w-[15%] overflow-hidden'>
            <div class='text-sm font-normal text-gray overflow-hidden text-ellipsis whitespace-nowrap'>
              <HighlightedText text={record?.sku_code} highlight={queryFilter?.search} />
            </div>
          </div>
        ),
      },
      {
        name: 'GS1 Barcode',
        className: 'w-[15%] text-sm font-semibold text-green',
        dataIndex: 'location',
        render: (record) => (
          <div class='w-[15%] overflow-hidden'>
            <div class='text-sm font-normal text-gray overflow-hidden text-ellipsis whitespace-nowrap'>
              <HighlightedText text={record?.gs_one_barcode} highlight={queryFilter?.search} />
            </div>
          </div>
        ),
      },
      {
        name: 'Harga',
        className: 'w-[12%] text-sm font-semibold text-green',
        dataIndex: '',
        render: (record) => (
          <div class='w-[12%]'>
            <div class='text-sm font-normal text-gray'>{convertToRupiah(record?.price)}</div>
          </div>
        ),
      },
      {
        name: 'Tgl Create',
        className: 'w-[12%] text-sm font-semibold text-green',
        dataIndex: '',
        render: (record) => (
          <div class='w-[12%]'>
            <div class='text-sm font-normal text-gray'>{record?.created_at_formatted}</div>
          </div>
        ),
      },
      {
        name: 'Tgl Exp',
        className: 'w-[10%] text-sm font-semibold text-green',
        dataIndex: '',
        render: (record) => (
          <div class='w-[10%]'>
            <div class='text-sm font-normal text-gray'>{record?.variants?.[0]?.expired_date_formatted}</div>
            {record?.variants && record?.variants.length > 1 && (
              <div class='text-xs font-normal italic text-gray'>{`and ${record.variants.length - 1} more`}</div>
            )}
          </div>
        ),
      },
      {
        name: null,
        className: 'w-[8%] text-sm font-semibold text-green',
        dataIndex: '',
        render: (record) => (
          <div class='w-[8%] flex justify-center'>
            {record?.variants && record?.variants.length > 1 && (
              <button
                class={clsx(
                  ' inline-block w-8 h-8 bg-green arrow-down text-white font-medium text-xs uppercase rounded-full focus:outline-none transition duration-150 ease-in-out ',
                  !expandedRowIds.includes(record.id) && 'collapsed'
                )}
                type='button'
                data-bs-toggle='collapse'
                data-bs-target={`#collapsed-row-${record.id}`}
                aria-expanded='false'
                onClick={() => {
                  if (expandedRowIds.includes(record.id)) {
                    setExpandedRowIds(expandedRowIds.filter((id) => id !== record.id));
                  } else {
                    setExpandedRowIds([...expandedRowIds, record.id]);
                  }
                }}
                aria-controls={`collapsed-row-${record.id}`}
              >
                <span class='icon-ico-chev-right text-lg'></span>
              </button>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryFilter?.search]
  );

  const resetCheckList = () => {
    if (selectedIds.length > 0) {
      dispatch(setToolsReducer({ selectedRowKeys: [], selectedRows: [] }));
    }
  };

  const handleExport = () => {
    exportProduct(
      {
        queryFilter: queryFilter,
        bodyFilter: { product_ids: selectedIds },
      },
      {
        onSuccess: (res) => {
          const link = document.createElement('a');
          link.href = res.data.download_url;
          document.body.appendChild(link);
          link.click();
          setModals({ ...modals, export: false });
        },
      }
    );
  };

  useEffect(() => {
    resetCheckList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList]);

  const renderRowExpand = (props, item) => {
    if (item.variants.length > 1) {
      // remove first item fromm item.variants
      const variants = item.variants.slice(1);

      return (
        <div class='collapse' id={`collapsed-row-${item.id}`}>
          <div class='flex items-center w-full px-2 py-3 bg-gray-3'>
            <div class='w-[9%]'>&nbsp;</div>
            <div class='w-[19%]'>
              <div class='block text-sm font-semibold text-green'>Expired Date</div>
            </div>

            {variants.map((variant, index) => (
              <div class='w-[15%]'>
                <div class='block text-sm font-medium text-dark'>{variant?.expired_date_formatted}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  return (
    <MainDashboardLayoutComponent breadCumbs={BREAD_CUMB_ITEMS}>
      <div className='flex items-center mb-5'>
        <div className='w-full flex items-center justify-between gap-x-2'>
          <TableSearchInputComponent
            containerClassName='w-[40%] mr-2 relative'
            placeholder='Cari nama product...'
            inputProps={{
              disabled: isLoading,
            }}
            initialValue={INITIAL_VALUE_FILTER_QUERY.search}
            onSearch={(value) => {
              setQueryFilter({ ...queryFilter, search: value.keyword, page: 1 });
              navigate({
                search: qs.stringify({
                  ...queryFilter,
                  search: value.keyword || undefined,
                  page: 1,
                }),
              });
            }}
          />

          <div className='w-full flex flex-row gap-2'>
            <TableButtonSortComponent
              disabled={isLoading}
              className=' outline-none flex items-center border border-gray-1 rounded-sm hover:bg-light-green transition hover:border-green duration-150 ease-in-out border-solid py-1 px-3'
              onClick={() => {
                setModals((prev) => ({ ...prev, sort: true }));
              }}
            />

            <TableButtonLimitComponent
              disabled={isLoading}
              className=' outline-none  relative flex items-center border border-gray-1 rounded-sm hover:bg-light-green transition hover:border-green duration-150 ease-in-out border-solid py-1 px-3'
              value={queryFilter.limit}
              onClick={() => setModals({ ...modals, limit: true })}
            />

            <TableButtonExport
              className=' outline-none flex items-center border border-gray-1 rounded-sm hover:bg-light-green transition hover:border-green duration-150 ease-in-out border-solid py-1 px-3'
              disabled={isLoading}
              loading={isExporting}
              onClick={() => {
                setModals({ ...modals, export: true });
              }}
            />
            <TableButtonImport
              className=' outline-none flex items-center border border-gray-1 rounded-sm hover:bg-light-green transition hover:border-green duration-150 ease-in-out border-solid py-1 px-3'
              disabled={isLoading}
              loading={isExporting}
              onClick={() => {
                navigate(PATH_CONSTANT.PRODUCT.PRODUCT_IMPORT);
              }}
            />
          </div>
        </div>
        <div className='w-[340px] flex items-center justify-end'>
          <div className='w-[215px]'>
            <ButtonSubmit
              disabled={isLoading}
              onClick={() => navigate(PATH_CONSTANT.PRODUCT.PRODUCT_CREATE)}
              className='bg-green w-full flex items-center justify-center hover:bg-dark-green transition duration-75 ease-in-out rounded-sm text-white py-2 font-semibold text-sm px-7'
            >
              <span class='icon-ico-plus-circle text-[20px] leading-[21px] mr-3'></span>
              Create Product
            </ButtonSubmit>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='h-[70vh] flex items-center'>
          <BounceLoading color='#5E755A' />
        </div>
      ) : (
        <>
          {Boolean(productList?.data?.total_result) && (
            <div className='italic text-dark text-sm mb-5'>{`Total ${productList?.data?.total_result} products`}</div>
          )}

          <BasicTableComponent
            withExpand={true}
            rowClassName='flex mb-3 items-center rounded-sm py-4 px-2 border border-solid border-transparent hover:border-green bg-white hover:bg-light-green transition duration-75 ease-in-out cursor-pointer'
            columns={columns}
            renderExpand={renderRowExpand}
            dataSource={productList?.data?.rows || []}
            loading={isLoading}
            error={isError}
            errorTitle={`${error?.response?.data?.status} - ${error?.response?.data?.code}`}
            errorMessage={catchErrorMessage(error?.response?.data?.message)}
          />

          {productList?.data?.total_result === 0 && (
            <EmptyStateComponent
              message={queryFilter.search ? `No result for query "${queryFilter.search}"` : 'No data available'}
            />
          )}

          <Pagination
            currentPage={queryFilter.page}
            pageSize={queryFilter.limit}
            siblingCount={1}
            totalCount={productList?.data?.total_result || 0}
            onPageChange={(page) => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
              setQueryFilter({ ...queryFilter, page });
              navigate({
                search: qs.stringify({
                  ...queryFilter,
                  page,
                }),
              });
            }}
          />
        </>
      )}
      <ModalTableSortComponent
        options={SORT_OPTIONS}
        initialValue={SORT_OPTIONS.find(
          (sortItem) => sortItem.sortBy === queryFilter.orderBy && sortItem.sortType === queryFilter.orderType
        )}
        onChange={({ selected }) => {
          setQueryFilter((prevState) => ({
            ...prevState,
            orderBy: selected.sortBy,
            orderType: selected.sortType,
          }));
          navigate({
            search: qs.stringify({
              ...queryFilter,
              orderBy: selected.sortBy,
              orderType: selected.sortType,
            }),
          });
          setModals({ ...modals, sort: false });
        }}
        visible={modals.sort}
        onClose={() => setModals({ ...modals, sort: false })}
      />

      <ModalTableLimitComponent
        onChange={({ limit }) => {
          setQueryFilter((prevState) => ({
            ...prevState,
            limit,
            page: 1,
          }));
          navigate({
            search: qs.stringify({
              ...queryFilter,
              limit,
              page: 1,
            }),
          });
          setModals({ ...modals, limit: false });
        }}
        initialValue={queryFilter.limit}
        visible={modals.limit}
        onClose={() => setModals({ ...modals, limit: false })}
      />

      <ModalConfirmation
        description='Apakah anda yakin ingin eksport data yang anda pilih ?'
        title='Export Data'
        visible={modals.export}
        onConfirm={handleExport}
        onClose={() => setModals({ ...modals, export: false })}
      />
    </MainDashboardLayoutComponent>
  );
};
